import { useState, useEffect, useMemo, useRef } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { fallAnalysisValidator } from "../fall-analysis/formUtils";
import { useAnalyticsWrapper, useStoreWrapper } from "../../../../store/store";
import useIncident from "../../../../hooks/useIncident";
import useSelectOptions from "../../../../hooks/useSelectOptions";
import { ALL_INCIDENTS_OPTION, mapIncidentTypeOptions } from "../../../../utils/helpers/analyticsUtils";

export const ALL_CAREGIVERS_OPTION = {
  label: "All Caregivers",
  value: "all_caregivers",
};

type UseCaregiverAuditFormParams = {
  submitFn: any;
  // Pre-populates the form with the report's current params (Modify mode).
  // Omit/leave undefined for a fresh "Generate" open.
  initialFacility?: any;
  initialStartDate?: string;
  initialEndDate?: string;
  initialIncidentTypeLabels?: string[];
  initialCaregiverIds?: string[];
};

// Caregiver Audit param form (MARCC-773): facility + date range + optional
// incident-type and caregiver filters. Mirrors the Fall form hook but also
// persists the two extra filters (facility persists as before).
export const useCaregiverAuditForm = ({
  submitFn,
  initialFacility,
  initialStartDate,
  initialEndDate,
  initialIncidentTypeLabels,
  initialCaregiverIds,
}: UseCaregiverAuditFormParams) => {
  const [facilityOptions, setFacilityOptions] = useState<any>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const {
    setStartDate,
    setEndDate,
    setCaregiverAuditFacility,
    setCaregiverAuditIncidentTypes,
    setCaregiverAuditCaregiverIds,
  } = useAnalyticsWrapper((state) => ({
    setStartDate: state.setStartDate,
    setEndDate: state.setEndDate,
    setCaregiverAuditFacility: state.setCaregiverAuditFacility,
    setCaregiverAuditIncidentTypes: state.setCaregiverAuditIncidentTypes,
    setCaregiverAuditCaregiverIds: state.setCaregiverAuditCaregiverIds,
  }));

  const { userFacilities } = useStoreWrapper((state) => ({
    userFacilities: state.userFacilities,
  }));

  const formik = useFormik({
    initialValues: {
      facilityInfo: "",
      startDate: "",
      endDate: "",
      incidentTypes: [] as any[],
      caregivers: [] as any[],
    },
    validationSchema: fallAnalysisValidator,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      if (formik.isValid) {
        setIsFetching(true);
        setTimeout(() => {
          setIsFetching(false);
          setCaregiverAuditFacility(values.facilityInfo as any);
          setStartDate(values.startDate);
          setEndDate(values.endDate);

          const pickedAllIncidentTypes = values.incidentTypes.some(
            (opt: any) => opt.value === ALL_INCIDENTS_OPTION.value
          );
          setCaregiverAuditIncidentTypes(
            pickedAllIncidentTypes
              ? []
              : values.incidentTypes.map((opt: any) => opt.label)
          );

          const pickedAllCaregivers = values.caregivers.some(
            (opt: any) => opt.value === ALL_CAREGIVERS_OPTION.value
          );
          setCaregiverAuditCaregiverIds(
            pickedAllCaregivers
              ? []
              : values.caregivers.map((opt: any) => opt.value)
          );

          submitFn();
        }, 1000);
      }
    },
  });

  const isSubmitDisabled =
    moment(formik.values.startDate).isAfter(moment(formik.values.endDate)) ||
    isFetching ||
    !formik.isValid;

  useEffect(() => {
    if (userFacilities?.length) {
      let options = userFacilities.map((facility: any) => {
        const { facilityName, facilityId } = facility;
        return { label: facilityName, value: facilityId };
      });
      options = options.sort((a: any, b: any) => (a.label > b.label ? 1 : -1));
      setFacilityOptions(options);
    }
  }, [userFacilities]);

  useEffect(() => {
    formik.validateForm();
  }, [formik.values]);

  const { getAllIncidentTypes } = useIncident();
  const { data: incidentTypesData } = getAllIncidentTypes;

  const incidentTypeOptions = useMemo(() => {
    const options = mapIncidentTypeOptions(incidentTypesData).map(
      ({ label, value }) => ({ label, value })
    );
    return [ALL_INCIDENTS_OPTION, ...options];
  }, [incidentTypesData]);

  // Caregiver options are facility-scoped: the same "staff in facility" source
  // (Contact_on_Facility__c) that populates the four role fields on the incident
  // form, so selecting one here filters correctly against those fields.
  const selectedFacilityId = (formik.values.facilityInfo as any)?.value || "";
  const { allLookUpOptions, loaders } = useSelectOptions(selectedFacilityId);

  const caregiverOptions = useMemo(() => {
    const options = (allLookUpOptions.staffInFacility || []).map(
      (opt: any) => ({ label: opt.label, value: opt.value })
    );
    return [ALL_CAREGIVERS_OPTION, ...options];
  }, [allLookUpOptions.staffInFacility]);

  // --- Modify-mode population (runs once) + facility-change clearing ---
  // Order matters: population must mark the facility as "already seen" before the
  // clear-on-change effect runs, so restoring the current facility isn't mistaken
  // for the user picking a new one.
  const prevFacilityIdRef = useRef<string | null>(null);
  const filtersInitializedRef = useRef(false);
  const caregiversInitializedRef = useRef(false);

  useEffect(() => {
    if (filtersInitializedRef.current) return;
    if (!initialFacility) return; // fresh "Generate" open — nothing to restore

    formik.setFieldValue("facilityInfo", initialFacility);
    if (initialStartDate) formik.setFieldValue("startDate", initialStartDate);
    if (initialEndDate) formik.setFieldValue("endDate", initialEndDate);

    const matchedTypes = initialIncidentTypeLabels?.length
      ? incidentTypeOptions.filter((opt) =>
          initialIncidentTypeLabels.includes(opt.label)
        )
      : [];
    formik.setFieldValue(
      "incidentTypes",
      matchedTypes.length ? matchedTypes : [ALL_INCIDENTS_OPTION]
    );

    prevFacilityIdRef.current = (initialFacility as any)?.value || null;
    filtersInitializedRef.current = true;
  }, [initialFacility, incidentTypeOptions]);

  useEffect(() => {
    if (caregiversInitializedRef.current) return;
    if (!initialFacility) return;

    if (!initialCaregiverIds?.length) {
      formik.setFieldValue("caregivers", [ALL_CAREGIVERS_OPTION]);
      caregiversInitializedRef.current = true;
      return;
    }

    // Wait for the facility-scoped catalog (more than just the "All" sentinel).
    if (caregiverOptions.length <= 1) return;

    const matched = caregiverOptions.filter((opt: any) =>
      initialCaregiverIds.includes(opt.value)
    );
    formik.setFieldValue(
      "caregivers",
      matched.length ? matched : [ALL_CAREGIVERS_OPTION]
    );
    caregiversInitializedRef.current = true;
  }, [initialFacility, initialCaregiverIds, caregiverOptions]);

  // A genuine facility change (not the initial restore) invalidates the prior
  // caregiver selection — those Contact_on_Facility__c Ids belong to another facility.
  useEffect(() => {
    const currentFacilityId = selectedFacilityId || null;
    if (
      prevFacilityIdRef.current !== null &&
      prevFacilityIdRef.current !== currentFacilityId
    ) {
      formik.setFieldValue("caregivers", []);
      caregiversInitializedRef.current = true; // supersede any pending restore
    }
    prevFacilityIdRef.current = currentFacilityId;
  }, [selectedFacilityId]);

  // Selecting "All" replaces the whole selection; picking any real option clears "All".
  const makeAllAwareChangeHandler =
    (fieldName: "incidentTypes" | "caregivers", allOption: { label: string; value: string }) =>
    (opt: any) => {
      const optionArray = Array.isArray(opt) ? opt : [];
      const pickedAll = optionArray.some((o: any) => o.value === allOption.value);
      formik.setFieldValue(fieldName, pickedAll ? [allOption] : optionArray);
    };

  return {
    facilityOptions,
    isFetching,
    setFieldValue: formik.setFieldValue,
    handleSubmit: formik.handleSubmit,
    values: formik.values,
    isValid: formik.isValid,
    isSubmitDisabled,
    incidentTypeOptions,
    onIncidentTypeChange: makeAllAwareChangeHandler("incidentTypes", ALL_INCIDENTS_OPTION),
    caregiverOptions,
    caregiversLoading: !!selectedFacilityId && !!loaders.staffInFacilityLoading,
    onCaregiverChange: makeAllAwareChangeHandler("caregivers", ALL_CAREGIVERS_OPTION),
  };
};
